import { useTranslation } from 'react-i18next';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { merge } from 'lodash';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { usePartialFormModule } from '../../utils/use-partial-form-module';
import type { SimpleSignupTranslationsPartialFormType } from './partial-forms/SimpleSignupTranslationsPartialForm';
import {
  SimpleSignupTranslationsPartialForm,
  SimpleSignupTranslationsTransformer,
} from './partial-forms/SimpleSignupTranslationsPartialForm';

export const MODULE_NAME = 'simple-signup';

export type SimpleSignupFormType = SimpleSignupTranslationsPartialFormType;

export const SimpleSignupFormTransformer: ModuleTransformer<SimpleSignupFormType> = {
  fromPayload: (args) => {
    return merge(SimpleSignupTranslationsTransformer.fromPayload(args));
  },
  toPayload: (args) => {
    return merge(SimpleSignupTranslationsTransformer.toPayload(args), {
      lottery: {
        dataSource: {
          dataSourceType: 'SIMPLE_SIGNUP',
        },
        simpleSignup: {},
      },
    });
  },
};

export type SimpleSignupFormProps = FormModuleProps<SimpleSignupFormType>;

export const SimpleSignupForm: React.FC<SimpleSignupFormProps> = ({
  templateId,
  initialValues,
  retailUnit,
  values,
  onChange: parentOnChange,
  onValidation: parentOnValidation,
  getFormState,
}) => {
  const { t } = useTranslation();

  const { onChange, validationHandlers } = usePartialFormModule({
    values: values ?? initialValues,
    numberOfPartialForms: 1,
    parentOnChange,
    parentOnValidation,
  });

  const {
    data: retailUnitData,
    isLoading: retailUnitIsLoading,
    error: retailUnitError,
  } = useGetRetailUnitQuery({ retailUnit });

  if (retailUnitIsLoading) {
    return (
      <Loading text={`${t('general-fetching')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (retailUnitError || !retailUnitData) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  return (
    <SimpleSignupTranslationsPartialForm
      moduleName={MODULE_NAME}
      templateId={templateId}
      initialValues={initialValues}
      retailUnit={retailUnit}
      retailUnitData={retailUnitData.retailUnit}
      values={values}
      onChange={onChange}
      onValidation={validationHandlers[0]}
      getFormState={getFormState}
    />
  );
};

export const module = {
  name: MODULE_NAME,
  transformer: SimpleSignupFormTransformer,
  form: SimpleSignupForm,
} as const;
